// Gold verified badge SVG (Instagram style, gold color)
export const isGoldVerified = (profile) => {
  const ids = ["24071a6691", "24071a6679"];
  const safeStr = (v) => (typeof v === 'string' || typeof v === 'number') ? String(v).toLowerCase() : "";
  const roll = safeStr(profile?.rollNo);
  const id = safeStr(profile?.id);
  const email = safeStr(profile?.email);
  const rollFromEmail = email.split("@")[0];
  return ids.includes(roll) || ids.includes(id) || ids.includes(rollFromEmail);
};

export function GoldVerifiedBadge({ style = {}, size = 20, title = "Verified" }) {
  return (
    <span title={title} className="verified-badge" style={style}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill="#FFD700" stroke="#B8860B" strokeWidth="2" />
        <path d="M8.5 12.5l2 2 4-4" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  User, Info, Award, Code, Briefcase, Users, Globe, Plus, Mail, Phone, MapPin, Trophy, Star,
  Github, ExternalLink, ImagePlus, Trash2, Save, Home, LogOut, Sun, Moon, Palette,
  Edit2, Check, X
} from "lucide-react";
import useEmojiReaction from '../utils/useEmojiReactions';
import { useTheme } from '../hooks/useTheme';
import API_URL from '../config';
import LoadingScreen from '../components/LoadingScreen';
import './Profile.css';

// ---------------------- Constants & Config ----------------------
const CP_PLATFORMS = [
  { key: "codechef", label: "CodeChef", buildUrl: (handle) => `https://www.codechef.com/users/${handle}` },
  { key: "hackerrank", label: "HackerRank", buildUrl: (handle) => `https://www.hackerrank.com/${handle}` },
  { key: "leetcode", label: "LeetCode", buildUrl: (handle) => `https://leetcode.com/${handle}` },
  { key: "codeforces", label: "Codeforces", buildUrl: (handle) => `https://codeforces.com/profile/${handle}` },
  { key: "atcoder", label: "AtCoder", buildUrl: (handle) => `https://atcoder.jp/users/${handle}` },
  { key: "github", label: "GitHub", buildUrl: (handle) => `https://github.com/${handle}` },
];

const DEFAULT_CP_HANDLES = CP_PLATFORMS.reduce((acc, { key }) => {
  acc[key] = { userId: "", url: "" };
  return acc;
}, {});

const getEmptyCpHandles = () => normalizeCpHandles({});

// ---------------------- Helper Utilities ----------------------
const looksLikeUrl = (value = "") => /^https?:\/\//i.test(String(value).trim());

const normalizeCpHandles = (rawHandles) => {
  const source = rawHandles && typeof rawHandles === "object" ? rawHandles : {};
  return CP_PLATFORMS.reduce((acc, { key }) => {
    const value = source[key];
    if (value && typeof value === "object") {
      const candidate = value || {};
      const userId = String(candidate.userId || "").trim();
      const link = String(candidate.url || candidate.link || "").trim();
      acc[key] = { userId, url: link };
    } else if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) {
        acc[key] = { ...DEFAULT_CP_HANDLES[key] };
      } else if (looksLikeUrl(trimmed)) {
        acc[key] = { userId: "", url: trimmed };
      } else {
        acc[key] = { userId: trimmed, url: "" };
      }
    } else {
      acc[key] = { ...DEFAULT_CP_HANDLES[key] };
    }
    return acc;
  }, {});
};

const deriveHandleUrl = (platformKey, { userId, url }) => {
  if (url && looksLikeUrl(url)) return url;
  if (userId) {
    const platform = CP_PLATFORMS.find((entry) => entry.key === platformKey);
    return platform ? platform.buildUrl(userId) : "";
  }
  return "";
};

const formatUserIdLabel = (raw) => {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return "";
  return trimmed.startsWith("@") ? trimmed : `@${trimmed}`;
};

const ensureHandleShape = (raw) => {
  if (raw && typeof raw === "object") {
    return { userId: String(raw.userId ?? ""), url: String(raw.url ?? raw.link ?? "") };
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return { userId: "", url: "" };
    if (looksLikeUrl(trimmed)) return { userId: "", url: trimmed };
    return { userId: trimmed, url: "" };
  }
  return { userId: "", url: "" };
};

const safeJsonParse = (val) => {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  try {
    const parsed = JSON.parse(val);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.warn("JSON parse error for value:", val, e);
    return [];
  }
};

const normalizeProfileFromServer = (rawProfile) => {
  if (!rawProfile || typeof rawProfile !== "object") return {};
  const { github, cpHandles, ...rest } = rawProfile;
  const normalizedHandles = normalizeCpHandles(cpHandles);
  if (github && typeof github === "string") {
    const trimmed = github.trim();
    if (trimmed) {
      if (!normalizedHandles.github.userId) normalizedHandles.github.userId = trimmed;
      if (!normalizedHandles.github.url) {
        normalizedHandles.github.url = CP_PLATFORMS.find((entry) => entry.key === "github")?.buildUrl(trimmed) || "";
      }
    }
  }
  return { ...rest, cpHandles: normalizedHandles };
};

const formatNameUpper = (value) => {
  if (!value) return "";
  return String(value).trim().replace(/\s+/g, " ").toUpperCase();
};

const deriveFromEmail = (email) => {
  if (!email) return { derivedName: "", derivedRoll: "" };
  const local = String(email).split("@")[0] || "";
  const looksLikeRoll = /\d/.test(local);
  let derivedName = "";
  let derivedRoll = "";
  if (looksLikeRoll && /^[A-Za-z0-9]+$/.test(local)) {
    derivedRoll = local;
  } else {
    const spaced = local.replace(/[._-]+/g, " ").split(" ").filter(Boolean).join(" ");
    derivedName = formatNameUpper(spaced || local);
  }
  return { derivedName, derivedRoll };
};

// ---------------------- Animation Variants ----------------------
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100
    }
  }
};

// New entrance variants for the whole page
const pageEntranceVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1
    }
  }
};

const headerVariants = {
  hidden: { y: -50, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 120, damping: 20 }
  }
};

const tabsVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 100, damping: 15, delay: 0.3 }
  }
};

const profilePicVariants = {
  hidden: { scale: 0.5, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { type: "spring", stiffness: 150, damping: 12, delay: 0.5 }
  }
};

const profileNameVariants = {
  hidden: { y: 10, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5, delay: 0.7 }
  }
};

// --- Granular Card & Element Variants ---
const cardContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3, // Cards appear one by one
      delayChildren: 0.9 // Start after header/tabs/profileSummary
    }
  }
};

const cardEntranceVariants = {
  hidden: { y: 30, opacity: 0, scale: 0.95 },
  visible: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 80,
      damping: 15,
      staggerChildren: 0.1, // Elements inside stagger
      delayChildren: 0.2
    }
  }
};

const elementEntranceVariants = {
  hidden: { y: 10, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.4, ease: "easeOut" }
  }
};

// ---------------------- Skeleton Components ----------------------
const ProfileSkeleton = () => (
  <div className="profile-page-wrapper skeleton-mode">
    <div className="page-header-container">
      <div className="skeleton-pulse skeleton-title"></div>
    </div>
    <main className="main-content-container">
      <div className="glass-header skeleton-pulse skeleton-header-card"></div>
      <div className="tabs-container glass-card skeleton-pulse skeleton-tabs"></div>
      <div className="personal-container">
        <div className="personal-avatar skeleton-pulse skeleton-avatar-card"></div>
        <div className="personal-main personal-card skeleton-pulse skeleton-info-card"></div>
      </div>
    </main>
  </div>
);

const formatListCleanly = (val) => {
  if (!val) return "";
  if (Array.isArray(val)) return val.filter(Boolean).join(", ");
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.filter(Boolean).join(", ");
        }
      } catch (e) {}
    }
    return val;
  }
  return String(val);
};

// ---------------------- Profile Component ----------------------
function Profile() {
  const [profile, setProfile] = useState({
    name: "", email: "", rollNo: "", department: "", section: "", year: "",
    contact: "", hobbies: "", interestsTechnical: "", interestsNonTechnical: "",
    aspirations: "", skills: [], certificates: [], technicalCertificates: [],
    nonTechnicalCertificates: [], cpHandles: getEmptyCpHandles(), internships: [],
    clubs: [], projects: [], bio: "", profile_pic: "", linkedin: "", linkedinUrl: "", city: "",
    motto: "", languages: "", strengths: "", achievements: "", technicalSkills: [],
    nonTechnicalSkills: [], role: "student", designation: "", work_dept: "", gender: "", isHosteller: "",
    school_name: "", junior_college: "", junior_college_location: "", current_locality: ""
  });

  const [connectionStatus, setConnectionStatus] = useState({ sent: "none", received: "none" });

  const [selectedTab, setSelectedTab] = useState("personal");
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newSkill, setNewSkill] = useState("");
  const [newCertificate, setNewCertificate] = useState("");
  const [newClubDetails, setNewClubDetails] = useState({ clubName: "", role: "", events: [] });
  const [editingClubIdx, setEditingClubIdx] = useState(null);
  const [editingClubData, setEditingClubData] = useState({ clubName: "", role: "", events: [] });
  const [newProject, setNewProject] = useState({ name: "", description: "", techStack: "", liveLink: "", repoLink: "" });
  const [newTechnicalCertificate, setNewTechnicalCertificate] = useState("");
  const [newNonTechnicalCertificate, setNewNonTechnicalCertificate] = useState("");
  const [newInternshipDetails, setNewInternshipDetails] = useState({ companyName: "", duration: "", role: "", description: "", type: "internship" });
  const [newLanguage, setNewLanguage] = useState("");
  const [newStrength, setNewStrength] = useState("");
  const [pendingProfilePic, setPendingProfilePic] = useState(null);
  const [isPhotoBusy, setIsPhotoBusy] = useState(false);
  const fileInputRef = useRef(null);

  const [connectionData, setConnectionData] = useState({ following: [], followers: [] });
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  useEffect(() => {
    if (selectedTab === 'connections') {
      fetch(`${API_URL}/connections/status?email=${profile.email}`)
        .then(res => res.json())
        .then(data => setConnectionData(data))
        .catch(err => console.error("Fetch connections error", err));
    }
  }, [selectedTab, profile.email]);

  const handleRemoveConnection = async (targetEmail) => {
    try {
      const res = await axios.post(`${API_URL}/connect/remove`, {
        userEmail: storedUser.email,
        targetEmail: targetEmail
      });
      if (res.data.success) {
        setConnectionData(prev => ({
          following: prev.following.filter(f => f.email !== targetEmail),
          followers: prev.followers.filter(f => f.email !== targetEmail)
        }));
        if (isViewingOther && targetEmail === profile.email) {
          setConnectionStatus({ sent: "none", received: "none" });
        }
      }
    } catch (err) {
      console.error("Remove connection error", err);
    }
  };

  const YEARS = ["1st year", "2nd year", "3rd year", "4th year"];
  const DEPARTMENTS = ["CSE", "CSE-AIML&IOT", "AIDS", "CSBS", "IT", "ECE", "EEE", "EIE", "MECH", "CIVIL", "BIOTECH", "DS", "CYS", "R&AI"];
  const DEFAULT_SECTIONS = ["A", "B", "C", "D"];
  const CSE_IOT_SECTIONS = ["A", "B", "C", "IOT"];
  const [departmentOther, setDepartmentOther] = useState("");
  const { theme, toggleTheme } = useTheme();

  const bioEmojis = useEmojiReaction('bio', profile.bio);
  const aspEmojis = useEmojiReaction('aspirations', profile.aspirations);
  const hobbyEmojis = useEmojiReaction('hobbies', profile.hobbies);
  const techEmojis = useEmojiReaction('interestsTechnical', profile.interestsTechnical);
  const nonTechEmojis = useEmojiReaction('interestsNonTechnical', profile.interestsNonTechnical);

  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("vjconnect_user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const { rollNo } = useParams();

  const requestedEmail = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("email");
  }, [location.search]);

  const targetEmail = rollNo ? rollNo + "@vnrvjiet.in" : requestedEmail || storedUser?.email || "";
  const isViewingOther = (rollNo || requestedEmail) && (!storedUser?.email || (rollNo ? rollNo + "@vnrvjiet.in" : requestedEmail) !== storedUser.email);

  useEffect(() => {
    if (isViewingOther) return;
    if (!storedUser || (!storedUser.name && !storedUser.email && !storedUser.rollNo)) return;
    const { derivedName, derivedRoll } = deriveFromEmail(storedUser.email || "");
    setProfile((p) => ({
      ...p,
      name: formatNameUpper(p.name || storedUser.name || derivedName || ""),
      email: p.email || storedUser.email || "",
      rollNo: p.rollNo || storedUser.rollNo || derivedRoll || "",
    }));
  }, [isViewingOther, storedUser]);

  useEffect(() => {
    if (!profile.department) {
      setDepartmentOther("");
      return;
    }
    if (DEPARTMENTS.includes(profile.department)) {
      setDepartmentOther("");
    } else {
      setDepartmentOther(profile.department);
    }
  }, [profile.department]);

  useEffect(() => {
    setSelectedTab("personal"); // Reset tab when navigating to a different profile
    const fetchProfile = async () => {
      setLoading(true);
      try {
        if (!targetEmail) {
          setLoading(false);
          // Demo mode mostly irrelevant here as targetEmail is expected, but handle graceful fallback
          if (!storedUser?.email) {
            console.warn("No logged-in user found. Redirecting to login.");
            navigate("/");
          }
          return;
        }

        const res = await axios.get(`${API_URL}/student/profile?email=${targetEmail}&requesterEmail=${storedUser?.email || ""}`);
        if (res.data.exists) {
          let profileName = res.data.profile.name || "";
          if (!profileName) {
            const emailParts = targetEmail.split('@')[0];
            const spaced = emailParts.split(/[._-]/).filter(Boolean).join(' ');
            profileName = formatNameUpper(spaced || emailParts);
          }
          const normalizedProfile = normalizeProfileFromServer(res.data.profile);
          const profileData = {
            ...normalizedProfile,
            name: formatNameUpper(profileName),
            rollNo: String(normalizedProfile.rollNo || ""),
            department: String(normalizedProfile.department || ""),
            section: String(normalizedProfile.section || ""),
            year: String(normalizedProfile.year || ""),
            contact: String(normalizedProfile.contact || ""),
            hobbies: formatListCleanly(normalizedProfile.hobbies),
            interestsTechnical: formatListCleanly(normalizedProfile.interestsTechnical),
            interestsNonTechnical: formatListCleanly(normalizedProfile.interestsNonTechnical),
            aspirations: formatListCleanly(normalizedProfile.aspirations),
            skills: safeJsonParse(normalizedProfile.skills),
            certificates: safeJsonParse(normalizedProfile.certificates),
            internships: safeJsonParse(normalizedProfile.internships),
            clubs: safeJsonParse(normalizedProfile.clubs),
            projects: safeJsonParse(normalizedProfile.projects), // Handle project JSON parsing safely
            bio: String(normalizedProfile.bio || ""),
            profile_pic: String(normalizedProfile.profile_pic || ""),
            cpHandles: normalizeCpHandles(normalizedProfile.cpHandles),
            linkedin: String(normalizedProfile.linkedin || ""),
            linkedinUrl: String(normalizedProfile.linkedinUrl || ""),
            city: String(normalizedProfile.city || ""),
            motto: String(normalizedProfile.motto || ""),
            languages: String(normalizedProfile.languages || ""),
            strengths: String(normalizedProfile.strengths || ""),
            achievements: String(normalizedProfile.achievements || ""),
            technicalSkills: safeJsonParse(normalizedProfile.technicalSkills),
            nonTechnicalSkills: safeJsonParse(normalizedProfile.nonTechnicalSkills),
            technicalCertificates: safeJsonParse(normalizedProfile.technicalCertificates),
            nonTechnicalCertificates: safeJsonParse(normalizedProfile.nonTechnicalCertificates),
            gender: String(normalizedProfile.gender || ""),
            isHosteller: String(normalizedProfile.isHosteller || ""),
            school_name: String(normalizedProfile.school_name || ""),
            junior_college: String(normalizedProfile.junior_college || ""),
            junior_college_location: String(normalizedProfile.junior_college_location || ""),
            current_locality: String(normalizedProfile.current_locality || ""),
          };
          setProfile((prev) => ({
            ...prev,
            ...profileData,
            role: res.data.role || (targetEmail.split('@')[0].match(/^\d/) ? "student" : "faculty"),
            designation: String(res.data.profile.designation || ""),
            work_dept: String(res.data.profile.work_dept || "")
          }));

          if (isViewingOther) {
            setConnectionStatus({
              sent: res.data.sentStatus || "none",
              received: res.data.receivedStatus || "none"
            });
          }
          setPendingProfilePic(null);
          setIsEditMode(false);
        } else {
          if (isViewingOther) {
            alert("This student hasn't shared their profile yet.");
            navigate(-1);
          } else {
            let profileName = storedUser?.name || "";
            if (!profileName) {
              const email = storedUser?.email || "";
              if (email) {
                const emailParts = email.split('@')[0];
                const spaced = emailParts.split(/[._-]/).filter(Boolean).join(' ');
                profileName = formatNameUpper(spaced || emailParts);
              }
            }
            setProfile((prev) => ({ ...prev, email: storedUser?.email || prev.email, name: formatNameUpper(profileName) }));
            setIsEditMode(true);
          }
        }
        setProfile((prev) => ({ ...prev, email: targetEmail }));
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [targetEmail, isViewingOther, navigate, storedUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleCpHandleChange = (platformKey, field, rawValue) => {
    setProfile((prev) => {
      const previousPlatform = prev.cpHandles?.[platformKey] || { ...DEFAULT_CP_HANDLES[platformKey] };
      const updatedPlatform = { ...previousPlatform, [field]: rawValue };
      return { ...prev, cpHandles: { ...prev.cpHandles, [platformKey]: updatedPlatform } };
    });
  };

  const handleProfilePicSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const MAX_BYTES = 1024 * 1024;
    if (file.size > MAX_BYTES) {
      alert("Please choose an image smaller than or equal to 1 MB.");
      event.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPendingProfilePic({
        preview: reader.result,
        dataUrl: reader.result,
        fileName: file.name,
        fileSize: file.size,
      });
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const openProfilePicPicker = () => {
    if (!isEditMode) return;
    fileInputRef.current?.click();
  };

  const uploadProfilePic = async () => {
    const MAX_BYTES = 1024 * 1024;
    if (!pendingProfilePic?.dataUrl || !profile.email) {
      alert("Select a photo before uploading.");
      return;
    }
    if (pendingProfilePic.fileSize && pendingProfilePic.fileSize > MAX_BYTES) {
      window.alert('Please upload an image below 1 MB.');
      return;
    }
    setIsPhotoBusy(true);
    try {
      const response = await axios.post(`${API_URL}/upload_profile_pic`, {
        email: profile.email,
        imageData: pendingProfilePic.dataUrl,
      });
      const savedPic = response.data?.profile_pic || pendingProfilePic.dataUrl;
      setProfile((prev) => ({ ...prev, profile_pic: savedPic }));
      setPendingProfilePic(null);
      try {
        const stored = JSON.parse(localStorage.getItem("vjconnect_user") || "{}");
        if (stored.email === profile.email) {
          localStorage.setItem("vjconnect_user", JSON.stringify({ ...stored, profile_pic: savedPic }));
        }
      } catch { /* ignore */ }
    } catch (error) {
      console.error("Failed to upload profile picture", error);
      let msg = "Failed to upload profile picture. Please try again.";
      if (error?.response?.data?.error) {
        msg = error.response.data.error;
      }
      window.alert(msg);
    } finally {
      setIsPhotoBusy(false);
    }
  };

  const removeProfilePic = async () => {
    if (!profile.email) return;
    setIsPhotoBusy(true);
    try {
      await axios.post(`${API_URL}/remove_profile_pic`, { email: profile.email });
      setProfile((prev) => ({ ...prev, profile_pic: "" }));
      setPendingProfilePic(null);
      try {
        const stored = JSON.parse(localStorage.getItem("vjconnect_user") || "{}");
        if (stored.email === profile.email) {
          localStorage.setItem("vjconnect_user", JSON.stringify({ ...stored, profile_pic: "" }));
        }
      } catch { /* ignore */ }
    } catch (error) {
      console.error("Failed to remove profile picture", error);
      alert("Failed to remove profile picture. Please try again.");
    } finally {
      setIsPhotoBusy(false);
    }
  };

  const addTechnicalCertificate = (cert) => {
    const t = String(cert || '').trim();
    if (!t) return;
    setProfile(prev => ({ ...prev, technicalCertificates: [...(prev.technicalCertificates || []), t] }));
    setNewTechnicalCertificate("");
  };

  const addNonTechnicalCertificate = (cert) => {
    const t = String(cert || '').trim();
    if (!t) return;
    setProfile(prev => ({ ...prev, nonTechnicalCertificates: [...(prev.nonTechnicalCertificates || []), t] }));
    setNewNonTechnicalCertificate("");
  };

  const removeCertificate = (index, type = 'both') => {
    setProfile(prev => {
      if (type === 'tech') return { ...prev, technicalCertificates: (prev.technicalCertificates || []).filter((_, i) => i !== index) };
      if (type === 'nontech') return { ...prev, nonTechnicalCertificates: (prev.nonTechnicalCertificates || []).filter((_, i) => i !== index) };
      return { ...prev, certificates: (prev.certificates || []).filter((_, i) => i !== index) };
    });
  };

  const addClub = (club) => {
    if (typeof club === 'string') {
      if (club.trim()) setProfile(prev => ({ ...prev, clubs: [...(prev.clubs || []), club.trim()] }));
    } else {
      if (club.clubName && club.clubName.trim()) setProfile(prev => ({ ...prev, clubs: [...(prev.clubs || []), club] }));
      setNewClubDetails({ clubName: "", role: "", events: [] });
    }
  };

  const removeClub = (index) => setProfile(prev => ({ ...prev, clubs: (prev.clubs || []).filter((_, i) => i !== index) }));

  const updateClub = (index, updatedClub) => {
    setProfile(prev => {
      const updated = [...(prev.clubs || [])];
      updated[index] = updatedClub;
      return { ...prev, clubs: updated };
    });
    setEditingClubIdx(null);
  };

  const addInternship = (internship) => {
    if (internship.companyName && internship.companyName.trim() && internship.role && internship.role.trim()) {
      setProfile(prev => ({ ...prev, internships: [...(prev.internships || []), internship] }));
      setNewInternshipDetails({ companyName: "", duration: "", role: "", description: "", type: "internship" });
    }
  };

  const removeInternship = (index) => setProfile(prev => ({ ...prev, internships: (prev.internships || []).filter((_, i) => i !== index) }));

  const addProject = (project) => {
    if (project.name.trim()) setProfile(prev => ({ ...prev, projects: [...(prev.projects || []), project] }));
    setNewProject({ name: "", description: "", techStack: "", liveLink: "", repoLink: "" });
  };

  const removeProject = (index) => setProfile(prev => ({ ...prev, projects: (prev.projects || []).filter((_, i) => i !== index) }));

  const parseList = (str) => String(str || "").split(',').map(s => s.trim()).filter(Boolean);

  const addLanguage = (value) => {
    const candidate = String(value || newLanguage || "").trim();
    if (!candidate) return;
    const list = parseList(profile.languages);
    if (list.includes(candidate)) { setNewLanguage(""); return; }
    const updated = [...list, candidate];
    setProfile(prev => ({ ...prev, languages: updated.join(', ') }));
    setNewLanguage("");
  };

  const removeLanguage = (index) => {
    const list = parseList(profile.languages);
    list.splice(index, 1);
    setProfile(prev => ({ ...prev, languages: list.join(', ') }));
  };

  const addStrength = (value) => {
    const candidate = String(value || newStrength || "").trim();
    if (!candidate) return;
    const list = parseList(profile.strengths);
    if (list.includes(candidate)) { setNewStrength(""); return; }
    const updated = [...list, candidate];
    setProfile(prev => ({ ...prev, strengths: updated.join(', ') }));
    setNewStrength("");
  };

  const removeStrength = (index) => {
    const list = parseList(profile.strengths);
    list.splice(index, 1);
    setProfile(prev => ({ ...prev, strengths: list.join(', ') }));
  };

  const handleSave = async () => {
    if (isViewingOther) return;
    try {
      let effectiveProfilePic = profile.profile_pic || "";
      if (pendingProfilePic && pendingProfilePic.dataUrl) {
        const MAX_BYTES = 1024 * 1024;
        if (pendingProfilePic.fileSize && pendingProfilePic.fileSize > MAX_BYTES) {
          alert('Selected image exceeds 1 MB. Please choose a smaller image.');
          return;
        }
        setIsPhotoBusy(true);
        try {
          const uploadResp = await axios.post(`${API_URL}/upload_profile_pic`, {
            email: profile.email,
            imageData: pendingProfilePic.dataUrl,
          });
          const savedPic = uploadResp.data?.profile_pic || pendingProfilePic.dataUrl;
          effectiveProfilePic = savedPic;
          setProfile((prev) => ({ ...prev, profile_pic: savedPic }));
          try {
            const stored = JSON.parse(localStorage.getItem("vjconnect_user") || "{}");
            if (stored.email === profile.email) {
              localStorage.setItem("vjconnect_user", JSON.stringify({ ...stored, profile_pic: savedPic }));
            }
          } catch { /* ignore */ }
          setPendingProfilePic(null);
        } catch (err) {
          console.error("Failed to upload pending profile picture during save:", err);
          let msg = "Failed to upload profile picture. Please try again.";
          if (err?.response?.data?.error) {
            msg = err.response.data.error;
          }
          window.alert(msg);
          setIsPhotoBusy(false);
          return;
        } finally {
          setIsPhotoBusy(false);
        }
      }

      const combinedSkills = profile.skills.slice();
      if (newSkill && newSkill.trim()) combinedSkills.push(newSkill.trim());
      const combinedCertificates = profile.certificates.slice();
      if (newCertificate && newCertificate.trim()) combinedCertificates.push(newCertificate.trim());
      const combinedClubs = (profile.clubs || []).slice();
      const combinedInternships = (profile.internships || []).slice();

      const handlesForSave = CP_PLATFORMS.reduce((acc, { key }) => {
        const entry = profile.cpHandles?.[key] || DEFAULT_CP_HANDLES[key];
        const userId = String(entry?.userId || "").trim();
        let url = String(entry?.url || "").trim();
        if (!url && userId) {
          url = deriveHandleUrl(key, { userId, url });
        }
        acc[key] = { userId, url };
        return acc;
      }, {});

      const effectiveDepartment = (profile.department === 'OTHERS') ? (departmentOther || '') : profile.department;
      const payload = {
        name: profile.name,
        email: profile.email,
        rollNo: profile.rollNo,
        department: effectiveDepartment,
        section: profile.section,
        year: profile.year,
        contact: profile.contact,
        hobbies: profile.hobbies,
        interestsTechnical: profile.interestsTechnical,
        interestsNonTechnical: profile.interestsNonTechnical,
        aspirations: profile.aspirations,
        skills: combinedSkills,
        certificates: combinedCertificates,
        internships: combinedInternships,
        clubs: combinedClubs,
        projects: profile.projects || [],
        cpHandles: handlesForSave,
        github: handlesForSave.github?.userId || "",
        bio: profile.bio,
        profile_pic: effectiveProfilePic || profile.profile_pic,
        linkedin: profile.linkedin || "",
        linkedinUrl: profile.linkedinUrl || "",
        city: profile.city || "",
        motto: profile.motto || "",
        languages: profile.languages || "",
        strengths: profile.strengths || "",
        achievements: profile.achievements || "",
        technicalSkills: profile.technicalSkills || [],
        nonTechnicalSkills: profile.nonTechnicalSkills || [],
        technicalCertificates: profile.technicalCertificates || [],
        nonTechnicalCertificates: profile.nonTechnicalCertificates || [],
        designation: profile.designation || "",
        work_dept: profile.work_dept || "",
        gender: profile.gender || "",
        isHosteller: profile.isHosteller || "",
        school_name: profile.school_name || "",
        junior_college: profile.junior_college || "",
        junior_college_location: profile.junior_college_location || "",
        current_locality: profile.current_locality || "",
      };


      if (payload.contact && payload.contact.trim().length > 0 && payload.contact.trim().length !== 10) {
        alert("Contact number must be exactly 10 digits.");
        return;
      }

      if (!payload.email) {
        const userFromStorage = JSON.parse(localStorage.getItem("vjconnect_user") || "{}");
        payload.email = userFromStorage.email || payload.email;
        payload.name = payload.name || userFromStorage.name || payload.name;
      }

      if (!payload.name) {
        const { derivedName, derivedRoll } = deriveFromEmail(payload.email || "");
        const fallbackName = derivedName || derivedRoll || (payload.email ? payload.email.split('@')[0] : "");
        payload.name = formatNameUpper(fallbackName);
      }

      const requiredFields = ['email', 'name', 'rollNo', 'department', 'year', 'gender'];
      for (const field of requiredFields) {
        if (!payload[field]) {
          alert(`Please fill in all required fields. Missing: ${field}`);
          return;
        }
      }

      const res = await axios.post(`${API_URL}/student/profile/update`, payload);
      if (res.data.success) {
        alert("Profile saved successfully!");
        try {
          const fetched = await axios.get(`${API_URL}/student/profile?email=${payload.email}`);
          if (fetched.data.exists) {
            const normalized = normalizeProfileFromServer(fetched.data.profile);
            setProfile(prev => ({ ...prev, ...normalized, cpHandles: normalizeCpHandles(normalized.cpHandles) }));
            setPendingProfilePic(null);
          }
        } catch { /* ignore */ }
        setIsEditMode(false);
        try {
          const stored = JSON.parse(localStorage.getItem("vjconnect_user") || "{}");
          if (stored.email === payload.email) {
            localStorage.setItem("vjconnect_user", JSON.stringify({
              ...stored,
              name: payload.name,
              profile_pic: payload.profile_pic,
              school_name: payload.school_name,
              junior_college: payload.junior_college,
              junior_college_location: payload.junior_college_location,
              current_locality: payload.current_locality,
              city: payload.city
            }));
          }
        } catch { /* ignore */ }
      } else {
        alert("Failed to save profile. Please check your input.");
      }
    } catch (error) {
      console.error("Error saving profile:", error);
      let msg = "Failed to save profile. Please try again.";
      if (error?.response?.data?.error) {
        msg = error.response.data.error;
      }
      window.alert(msg);
    }
  };

  const handleConnect = async () => {
    try {
      const res = await axios.post(`${API_URL}/connect/request`, {
        senderEmail: storedUser.email,
        senderName: storedUser.name,
        recipientEmail: profile.email
      });
      if (res.data.success) {
        setConnectionStatus(prev => ({ ...prev, sent: "pending" }));
        alert("Connection request sent!");
      }
    } catch (err) {
      console.error("Connect error", err);
    }
  };

  const handleAcceptConnect = async (targetEmail = null) => {
    const requesterEmail = targetEmail || profile.email;
    try {
      const res = await axios.post(`${API_URL}/connect/accept`, {
        accepterEmail: storedUser.email,
        accepterName: storedUser.name,
        requesterEmail: requesterEmail
      });
      if (res.data.success) {
        if (!targetEmail) {
          setConnectionStatus(prev => ({ ...prev, received: "accepted" }));
          // Re-fetch to get contact info
          window.location.reload();
        } else {
          // If called from Network tab, refresh data
          fetch(`${API_URL}/connections/status?email=${profile.email}`)
            .then(res => res.json())
            .then(data => setConnectionData(data));
        }
      }
    } catch (err) {
      console.error("Accept error", err);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading Profile" />;
  }

  const tabs = [
    { id: "personal", label: "Personal", icon: User },
    { id: "about", label: "About", icon: Info },
    { id: "skills", label: "Skills", icon: Award },
    { id: "programming", label: "Coding", icon: Code },
    { id: "professional", label: "Work", icon: Briefcase },
    { id: "clubs", label: "Clubs", icon: Globe },
    { id: "connections", label: "Network", icon: Users },
  ];


  const languagesList = parseList(profile.languages);
  const strengthsList = parseList(profile.strengths);
  const sectionOptions = profile.department === "CSE-AIML&IOT" ? CSE_IOT_SECTIONS : DEFAULT_SECTIONS;

  const renderTabContent = () => {
    switch (selectedTab) {
      case "personal":
        return (
          <motion.div
            variants={cardContainerVariants}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, y: -20 }}
            className="profile-container"
          >
            {/* Avatar Section */}
            <motion.div variants={cardEntranceVariants} className="profile-avatar-section">
              <motion.div variants={profilePicVariants} className={`profile-avatar-wrap ${isEditMode ? 'cursor-pointer' : 'cursor-zoom-in'}`} onClick={() => !isEditMode && setIsImageModalOpen(true)}>
                <div className="profile-avatar-inner">
                  {(pendingProfilePic?.preview || profile.profile_pic) ? (
                    <img src={pendingProfilePic?.preview || profile.profile_pic} alt="Profile" />
                  ) : (
                    <span className="profile-avatar-initial">{(profile.name || profile.email || '?').charAt(0).toUpperCase()}</span>
                  )}
                </div>
              </motion.div>

              {!isEditMode && (
                <motion.div variants={profileNameVariants} className="text-center">
                  <h2 className="profile-name-display gradient-text">
                    {profile.name || "Your Name"}
                    {isGoldVerified(profile) && <GoldVerifiedBadge />}
                  </h2>
                  <div className="profile-info-display mt-2">
                    {profile.email && <span className="profile-info-item"><Mail /> {profile.email}</span>}
                    {profile.city && <span className="profile-info-item"><MapPin /> {profile.city}</span>}
                  </div>

                  {isViewingOther && (
                    <div className="mt-4">
                      {connectionStatus.sent === 'accepted' && connectionStatus.received === 'accepted' ? (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <button disabled className="btn-profile btn-profile-secondary">Mutual Connection 🤝</button>
                          <button onClick={() => handleRemoveConnection(profile.email)} className="btn-profile btn-profile-danger" style={{ padding: '8px 12px' }}><Trash2 size={16} /></button>
                        </div>
                      ) : connectionStatus.sent === 'accepted' ? (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <button disabled className="btn-profile btn-profile-secondary">Connected ✅</button>
                          <button onClick={() => handleRemoveConnection(profile.email)} className="btn-profile btn-profile-danger" style={{ padding: '8px 12px' }}><Trash2 size={16} /></button>
                        </div>
                      ) : connectionStatus.sent === 'pending' ? (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <button disabled className="btn-profile btn-profile-secondary">Request Sent ⏳</button>
                          <button onClick={() => handleRemoveConnection(profile.email)} className="btn-profile btn-profile-danger" style={{ padding: '8px 12px' }}>Cancel</button>
                        </div>
                      ) : connectionStatus.received === 'accepted' ? (
                        <button onClick={handleConnect} className="btn-profile btn-profile-primary">Connect Back 🔗</button>
                      ) : connectionStatus.received === 'pending' ? (
                        <div style={{ display: 'flex', gap: 10 }}>
                          <button onClick={handleAcceptConnect} className="btn-profile btn-profile-primary">Accept Request ✅</button>
                          <button onClick={() => handleRemoveConnection(profile.email)} className="btn-profile btn-profile-danger">Reject</button>
                        </div>
                      ) : (
                        <button onClick={handleConnect} className="btn-profile btn-profile-primary">Connect 🔗</button>
                      )}
                    </div>
                  )}
                </motion.div>
              )}

              {isEditMode && (
                <motion.div variants={elementEntranceVariants} className="profile-avatar-actions">
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleProfilePicSelect} style={{ display: 'none' }} />
                  <button type="button" onClick={openProfilePicPicker} disabled={isPhotoBusy} className="btn-profile btn-profile-secondary">
                    <ImagePlus size={16} /> Choose
                  </button>
                  <button type="button" onClick={uploadProfilePic} disabled={isPhotoBusy || !pendingProfilePic?.dataUrl} className="btn-profile btn-profile-primary">
                    Upload
                  </button>
                  <button type="button" onClick={removeProfilePic} disabled={isPhotoBusy || !(profile.profile_pic || pendingProfilePic)} className="btn-profile btn-profile-danger">
                    <Trash2 size={16} /> Remove
                  </button>
                </motion.div>
              )}
            </motion.div>

            {/* Basic Info Card */}
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><User /> Basic Information</h3>
              <div className="profile-form-grid">
                <motion.div variants={elementEntranceVariants} className="profile-form-row">
                  <label className="profile-label">Full Name{isEditMode && " *"}</label>
                  <input type="text" name="name" value={profile.name} onChange={handleChange} disabled={!isEditMode} className="profile-input" placeholder="Your full name" />
                </motion.div>
                <motion.div variants={elementEntranceVariants} className="profile-form-row">
                  <label className="profile-label">Email</label>
                  <input type="email" name="email" value={profile.email} disabled className="profile-input" />
                </motion.div>
                {profile.role !== 'faculty' && (
                  <>
                    <motion.div variants={elementEntranceVariants} className="profile-form-row">
                      <label className="profile-label">Roll Number{isEditMode && " *"}</label>
                      <input type="text" name="rollNo" value={profile.rollNo} onChange={handleChange} disabled={!isEditMode} className="profile-input" placeholder="e.g., 21BCE7001" />
                    </motion.div>
                    <motion.div variants={elementEntranceVariants} className="profile-form-row">
                      <label className="profile-label">Year{isEditMode && " *"}</label>
                      <select name="year" value={profile.year} onChange={handleChange} disabled={!isEditMode} className="profile-select">
                        <option value="">Select Year</option>
                        {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </motion.div>
                  </>
                )}
                {profile.role === 'faculty' && (
                  <>
                    <motion.div variants={elementEntranceVariants} className="profile-form-row">
                      <label className="profile-label">Designation{isEditMode && " *"}</label>
                      <input type="text" name="designation" value={profile.designation} onChange={handleChange} disabled={!isEditMode} className="profile-input" placeholder="e.g. Assistant Professor" />
                    </motion.div>
                    <motion.div variants={elementEntranceVariants} className="profile-form-row">
                      <label className="profile-label">Work Department{isEditMode && " *"}</label>
                      <input type="text" name="work_dept" value={profile.work_dept} onChange={handleChange} disabled={!isEditMode} className="profile-input" placeholder="e.g. Dept of CSE" />
                    </motion.div>
                  </>
                )}
                <motion.div variants={elementEntranceVariants} className="profile-form-row">
                  <label className="profile-label">Gender{isEditMode && " *"}</label>
                  <select name="gender" value={profile.gender} onChange={handleChange} disabled={!isEditMode} className="profile-select">
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </motion.div>
                <motion.div variants={elementEntranceVariants} className="profile-form-row">
                  <label className="profile-label">Department{isEditMode && " *"}</label>
                  <select name="department" value={DEPARTMENTS.includes(profile.department) ? profile.department : 'OTHERS'} onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'OTHERS') {
                      setProfile(prev => ({ ...prev, department: '' }));
                    } else {
                      setProfile(prev => ({ ...prev, department: val }));
                    }
                  }} disabled={!isEditMode} className="profile-select">
                    <option value="">Select Department</option>
                    {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                    <option value="OTHERS">Other</option>
                  </select>
                  {(!DEPARTMENTS.includes(profile.department) && profile.department !== '') && isEditMode && (
                    <input type="text" value={departmentOther} onChange={(e) => { setDepartmentOther(e.target.value); setProfile(prev => ({ ...prev, department: e.target.value })); }} className="profile-input mt-2" placeholder="Enter department" />
                  )}
                </motion.div>
                {profile.role !== 'faculty' && (
                  <>
                    <motion.div variants={elementEntranceVariants} className="profile-form-row">
                      <label className="profile-label">Section</label>
                      <select name="section" value={profile.section} onChange={handleChange} disabled={!isEditMode} className="profile-select">
                        <option value="">Select Section</option>
                        {sectionOptions.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </motion.div>
                    <motion.div variants={elementEntranceVariants} className="profile-form-row">
                      <label className="profile-label">Hosteller</label>
                      <select name="isHosteller" value={profile.isHosteller || ""} onChange={handleChange} disabled={!isEditMode} className="profile-select">
                        <option value="">Select Option</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </motion.div>
                  </>
                )}
                <motion.div variants={elementEntranceVariants} className="profile-form-row">
                  <label className="profile-label">
                    Contact Number
                    <span 
                      title="Your number is only visible only if you connect each other"
                      style={{ fontSize: '0.75rem', color: '#8b5cf6', marginLeft: '0.5rem', fontWeight: 'normal', cursor: 'help' }}
                    >
                      🔒 (Visible only to connections)
                    </span>
                  </label>
                  {isEditMode ? (
                    <div>
                      <input
                        type="text"
                        name="contact"
                        value={profile.contact || ""}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                          handleChange({ target: { name: 'contact', value: val } });
                        }}
                        className="profile-input"
                        placeholder="10-digit mobile number"
                        maxLength={10}
                        pattern="\d{10}"
                      />
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
                        🔒 Your phone number is only visible to people who connect with you.
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 items-center" style={{ width: '100%' }}>
                      {profile.contact ? (
                        <>
                          <span className="profile-input" style={{ flex: 1 }}>{profile.contact}</span>
                          <a
                            href={`tel:${profile.contact}`}
                            className="btn-profile btn-profile-secondary flex items-center justify-center"
                            style={{ textDecoration: 'none', display: 'inline-flex', padding: '10px 14px' }}
                            title="Call"
                          >
                            📞
                          </a>
                        </>
                      ) : (
                        <span 
                          className="profile-input text-gray-400" 
                          title="Your number is only visible only if you connect each other"
                          style={{ cursor: 'help', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                          🔒 Phone number visible only if you connect with each other
                        </span>
                      )}
                    </div>
                  )}
                </motion.div>
                <motion.div variants={elementEntranceVariants} className="profile-form-row">
                  <label className="profile-label">City / Location</label>
                  <input type="text" name="city" value={profile.city} onChange={handleChange} disabled={!isEditMode} className="profile-input" placeholder="e.g. Hyderabad" />
                </motion.div>
                {profile.role === 'student' && (
                  <>
                    <motion.div variants={elementEntranceVariants} className="profile-form-row">
                      <label className="profile-label">School Name 🏫</label>
                      <input type="text" name="school_name" value={profile.school_name || ''} onChange={e => handleChange({ target: { name: 'school_name', value: e.target.value.toUpperCase() } })} disabled={!isEditMode} className="profile-input" placeholder="e.g. TRIVENI TALENT SCHOOL" />
                    </motion.div>
                    <motion.div variants={elementEntranceVariants} className="profile-form-row">
                      <label className="profile-label">Junior College 🎓</label>
                      <input type="text" name="junior_college" value={profile.junior_college || ''} onChange={e => handleChange({ target: { name: 'junior_college', value: e.target.value.toUpperCase() } })} disabled={!isEditMode} className="profile-input" placeholder="e.g. NARAYANA JUNIOR COLLEGE" />
                    </motion.div>
                    <motion.div variants={elementEntranceVariants} className="profile-form-row">
                      <label className="profile-label">Junior College Location</label>
                      <input type="text" name="junior_college_location" value={profile.junior_college_location || ''} onChange={e => handleChange({ target: { name: 'junior_college_location', value: e.target.value.toUpperCase() } })} disabled={!isEditMode} className="profile-input" placeholder="e.g. KUKATPALLY, HYDERABAD" />
                    </motion.div>
                    <motion.div variants={elementEntranceVariants} className="profile-form-row">
                      <label className="profile-label">Current Locality 📍</label>
                      <input type="text" name="current_locality" value={profile.current_locality || ''} onChange={e => handleChange({ target: { name: 'current_locality', value: e.target.value.toUpperCase() } })} disabled={!isEditMode} className="profile-input" placeholder="e.g. MIYAPUR, HYDERABAD" />
                    </motion.div>
                  </>
                )}
              </div>
            </motion.div>


            {/* Social Links */}
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><ExternalLink /> Social & Portfolio</h3>
              <div className="profile-form-grid">
                <motion.div variants={elementEntranceVariants} className="profile-form-row full-width">
                  <label className="profile-label">LinkedIn Profile</label>
                  {isEditMode ? (
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        name="linkedin"
                        value={profile.linkedin}
                        onChange={handleChange}
                        className="profile-input"
                        placeholder="Username (e.g., john-doe-123)"
                      />
                      <input
                        type="text"
                        name="linkedinUrl"
                        value={profile.linkedinUrl || ''}
                        onChange={handleChange}
                        className="profile-input"
                        placeholder="Full URL (e.g., https://linkedin.com/in/john-doe-123)"
                      />
                    </div>
                  ) : (
                    <div className="flex gap-2 items-center">
                      {profile.linkedin ? (
                        <>
                          <span className="profile-input" style={{ flex: 1 }}>{profile.linkedin}</span>
                          <a
                            href={profile.linkedinUrl || `https://linkedin.com/in/${profile.linkedin}`}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-profile btn-profile-secondary flex items-center justify-center"
                          >
                            <ExternalLink size={16} />
                          </a>
                        </>
                      ) : (
                        <span className="profile-input text-gray-400">Not provided</span>
                      )}
                    </div>
                  )}
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        );

      case "about":
        return (
          <motion.div
            variants={cardContainerVariants}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, y: -20 }}
            className="profile-container"
          >
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header">
                <Info /> Bio & Personality {isEditMode && bioEmojis}
              </h3>
              <motion.div variants={elementEntranceVariants} className="profile-form-row full-width">
                <label className="profile-label">Bio / Summary</label>
                <textarea name="bio" value={profile.bio} onChange={handleChange} disabled={!isEditMode} className="profile-textarea profile-textarea-scrollable" placeholder="Tell us about yourself..." />
              </motion.div>
            </motion.div>

            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header">
                <Star /> Aspirations {isEditMode && aspEmojis}
              </h3>
              <motion.div variants={elementEntranceVariants} className="profile-form-row full-width">
                <label className="profile-label">Your Goals & Aspirations</label>
                {isEditMode ? (
                  <textarea name="aspirations" value={profile.aspirations} onChange={handleChange} className="profile-textarea profile-textarea-scrollable" placeholder="What do you want to achieve?" />
                ) : (
                  <div className="profile-tags" style={{ marginTop: '0.4rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {formatListCleanly(profile.aspirations).split(',').filter(s => s && s.trim()).map((asp, idx) => (
                      <span key={idx} className="profile-tag profile-tag-tech">
                        ✨ {asp.trim()}
                      </span>
                    ))}
                    {!profile.aspirations && <span className="profile-input text-gray-400">Not provided</span>}
                  </div>
                )}
              </motion.div>
            </motion.div>

            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header">
                <Trophy /> Motto
              </h3>
              <motion.div variants={elementEntranceVariants} className="profile-form-row full-width">
                <label className="profile-label">Your Life Motto</label>
                <input type="text" name="motto" value={profile.motto} onChange={handleChange} disabled={!isEditMode} className="profile-input" placeholder="e.g. Dream big, work hard." />
              </motion.div>
            </motion.div>

            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Award /> Achievements</h3>
              <motion.div variants={elementEntranceVariants} className="profile-form-row full-width">
                <label className="profile-label">List your key achievements</label>
                <textarea name="achievements" value={profile.achievements} onChange={handleChange} disabled={!isEditMode} className="profile-textarea" placeholder="Hackathons won, Papers published..." />
              </motion.div>
            </motion.div>

            {/* Languages & Strengths */}
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Briefcase /> Languages & Strengths</h3>

              <motion.div variants={elementEntranceVariants} className="profile-form-row">
                <label className="profile-label">Languages Known</label>
                <div className="profile-tags">
                  {languagesList.map((lang, idx) => (
                    <span key={idx} className="profile-tag profile-tag-tech">
                      {lang}
                      {isEditMode && <button onClick={() => removeLanguage(idx)} className="profile-tag-remove">×</button>}
                    </span>
                  ))}
                </div>
                {isEditMode && (
                  <div className="profile-add-row">
                    <input type="text" value={newLanguage} onChange={(e) => setNewLanguage(e.target.value)} className="profile-input" placeholder="Add language..." onKeyDown={(e) => e.key === 'Enter' && addLanguage()} />
                    <button onClick={() => addLanguage()} className="btn-profile btn-profile-secondary"><Plus size={16} /></button>
                  </div>
                )}
              </motion.div>

              <motion.div variants={elementEntranceVariants} className="profile-form-row">
                <label className="profile-label">Key Strengths</label>
                <div className="profile-tags">
                  {strengthsList.map((str, idx) => (
                    <span key={idx} className="profile-tag profile-tag-tech">
                      {str}
                      {isEditMode && <button onClick={() => removeStrength(idx)} className="profile-tag-remove">×</button>}
                    </span>
                  ))}
                </div>
                {isEditMode && (
                  <div className="profile-add-row">
                    <input type="text" value={newStrength} onChange={(e) => setNewStrength(e.target.value)} className="profile-input" placeholder="Add strength..." onKeyDown={(e) => e.key === 'Enter' && addStrength()} />
                    <button onClick={() => addStrength()} className="btn-profile btn-profile-secondary"><Plus size={16} /></button>
                  </div>
                )}
              </motion.div>
            </motion.div>

            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header">
                <LogOut /> Hobbies {isEditMode && hobbyEmojis}
              </h3>
              <motion.div variants={elementEntranceVariants} className="profile-form-row full-width">
                <label className="profile-label">Activities & Interests</label>
                {isEditMode ? (
                  <textarea name="hobbies" value={profile.hobbies} onChange={handleChange} className="profile-textarea" placeholder="Reading, Gaming, Travels..." />
                ) : (
                  <div className="profile-tags" style={{ marginTop: '0.4rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {formatListCleanly(profile.hobbies).split(',').filter(s => s && s.trim()).map((hob, idx) => (
                      <span key={idx} className="profile-tag profile-tag-tech">
                        🎯 {hob.trim()}
                      </span>
                    ))}
                    {!profile.hobbies && <span className="profile-input text-gray-400">Not provided</span>}
                  </div>
                )}
              </motion.div>
            </motion.div>
          </motion.div>
        );

      case "skills":
        return (
          <motion.div
            variants={cardContainerVariants}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, y: -20 }}
            className="profile-container"
          >
            {/* Technical Certifications */}
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Award /> Technical Certifications</h3>
              <motion.div variants={containerVariants} className="profile-list">
                {(Array.isArray(profile.technicalCertificates) ? profile.technicalCertificates : []).map((cert, idx) => (
                  <motion.div variants={itemVariants} key={idx} className="profile-list-item">
                    <div className="profile-list-item-header">
                      <span className="profile-list-item-title">{cert}</span>
                      {isEditMode && <button onClick={() => removeCertificate(idx, 'tech')} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>}
                    </div>
                  </motion.div>
                ))}

                {isEditMode && (
                  <motion.div variants={elementEntranceVariants} className="profile-new-item-form mt-4">
                    <div className="profile-add-row">
                      <input type="text" value={newTechnicalCertificate} onChange={(e) => setNewTechnicalCertificate(e.target.value)} className="profile-input" placeholder="Add technical certificate" />
                      <button onClick={() => addTechnicalCertificate(newTechnicalCertificate)} className="btn-profile btn-profile-accent">Add</button>
                    </div>
                  </motion.div>
                )}

                {(!profile.technicalCertificates?.length && !isEditMode) && <p className="profile-view-empty">No technical certificates listed.</p>}
              </motion.div>
            </motion.div>

            {/* Non-Technical Certifications */}
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Award /> Non-Technical Certifications</h3>
              <motion.div variants={containerVariants} className="profile-list">
                {(Array.isArray(profile.nonTechnicalCertificates) ? profile.nonTechnicalCertificates : []).map((cert, idx) => (
                  <motion.div variants={itemVariants} key={idx} className="profile-list-item">
                    <div className="profile-list-item-header">
                      <span className="profile-list-item-title">{cert}</span>
                      {isEditMode && <button onClick={() => removeCertificate(idx, 'nontech')} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>}
                    </div>
                  </motion.div>
                ))}

                {isEditMode && (
                  <motion.div variants={elementEntranceVariants} className="profile-new-item-form mt-4">
                    <div className="profile-add-row">
                      <input type="text" value={newNonTechnicalCertificate} onChange={(e) => setNewNonTechnicalCertificate(e.target.value)} className="profile-input" placeholder="Add non-technical certificate" />
                      <button onClick={() => addNonTechnicalCertificate(newNonTechnicalCertificate)} className="btn-profile btn-profile-accent">Add</button>
                    </div>
                  </motion.div>
                )}

                {(!profile.nonTechnicalCertificates?.length && !isEditMode) && <p className="profile-view-empty">No non-technical certificates listed.</p>}
              </motion.div>
            </motion.div>

            {/* Tech Interests */}
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Info /> Technical Interests</h3>
              <motion.div variants={elementEntranceVariants} className="profile-form-row full-width">
                <label className="profile-label">Fields of Interest</label>
                <textarea name="interestsTechnical" value={profile.interestsTechnical} onChange={handleChange} disabled={!isEditMode} className="profile-textarea" placeholder="AI/ML, Web Development, Cloud..." />
              </motion.div>
            </motion.div>

            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Info /> Non-Technical Interests</h3>
              <motion.div variants={elementEntranceVariants} className="profile-form-row full-width">
                <label className="profile-label">Areas of Interest</label>
                <textarea name="interestsNonTechnical" value={profile.interestsNonTechnical} onChange={handleChange} disabled={!isEditMode} className="profile-textarea" placeholder="Photography, Music, Cooking..." />
              </motion.div>
            </motion.div>
          </motion.div>
        );

      case "connections":
        return (
          <motion.div variants={cardContainerVariants} initial="hidden" animate="visible" exit={{ opacity: 0, y: -20 }} className="profile-container">
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Users /> Following</h3>
              <div className="profile-list">
                {connectionData.following.length === 0 ? <p className="profile-view-empty">{isViewingOther ? `${profile.name} isn't following anyone yet.` : "You haven't requested to connect with anyone yet."}</p> : (
                  connectionData.following.map(f => (
                    <div key={f.email} className="profile-list-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div onClick={() => navigate(`/${f.email.split('@')[0]}`)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', overflow: 'hidden' }}>
                          {f.profile_pic ? <img src={f.profile_pic} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (f.name || '?').charAt(0)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{f.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>{f.status === 'pending' ? 'Request Sent' : 'Connected'}</div>
                        </div>
                      </div>
                      {!isViewingOther && (
                        <button onClick={() => handleRemoveConnection(f.email)} style={{ padding: '4px 8px', background: '#ff6b6b', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.75rem' }}>
                          {f.status === 'pending' ? 'Cancel' : 'Remove'}
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </motion.div>

            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Users /> Followers</h3>
              <div className="profile-list">
                {connectionData.followers.length === 0 ? <p className="profile-view-empty">{isViewingOther ? `No one has connected with ${profile.name} yet.` : "No one has connected with you yet."}</p> : (
                  connectionData.followers.map(f => (
                    <div key={f.email} className="profile-list-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div onClick={() => navigate(`/${f.email.split('@')[0]}`)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', overflow: 'hidden' }}>
                          {f.profile_pic ? <img src={f.profile_pic} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (f.name || '?').charAt(0)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{f.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>{f.status === 'pending' ? 'sent a request' : 'Connection Accepted'}</div>
                        </div>
                      </div>
                      {!isViewingOther && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          {f.status === 'pending' && (
                            <button onClick={() => handleAcceptConnect(f.email)} style={{ padding: '4px 8px', background: '#4ecdc4', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.75rem' }}>
                              Accept
                            </button>
                          )}
                          <button onClick={() => handleRemoveConnection(f.email)} style={{ padding: '4px 8px', background: '#eee', color: '#333', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.75rem' }}>
                            {f.status === 'pending' ? 'Reject' : 'Remove'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </motion.div>
        );
      case "programming":
        return (
          <motion.div
            variants={cardContainerVariants}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, y: -20 }}
            className="profile-container"
          >
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Code /> Competitive Programming Profiles</h3>


              <div className="profile-form-grid">
                {CP_PLATFORMS.map((platform) => {
                  if (platform.key === 'github') return null;
                  const handleData = profile.cpHandles[platform.key] || { userId: "", url: "" };
                  const { userId, url } = handleData;
                  const displayUrl = url || deriveHandleUrl(platform.key, handleData);
                  return (
                    <motion.div variants={elementEntranceVariants} key={platform.key} className="profile-form-row">
                      <label className="profile-label">{platform.label}</label>
                      {isEditMode ? (
                        <div className="flex flex-col gap-2">
                          <input
                            type="text"
                            value={userId}
                            onChange={(e) => handleCpHandleChange(platform.key, 'userId', e.target.value)}
                            className="profile-input"
                            placeholder={`Username (e.g., user123)`}
                          />
                          <input
                            type="text"
                            value={url}
                            onChange={(e) => handleCpHandleChange(platform.key, 'url', e.target.value)}
                            className="profile-input"
                            placeholder={`Full URL (e.g., ${platform.buildUrl('user123')})`}
                          />
                        </div>
                      ) : (
                        <div className="flex gap-2 items-center">
                          {userId ? (
                            <>
                              <span className="profile-input" style={{ flex: 1 }}>{userId}</span>
                              <a
                                href={displayUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="btn-profile btn-profile-secondary flex items-center justify-center"
                              >
                                <ExternalLink size={16} />
                              </a>
                            </>
                          ) : (
                            <span className="profile-input text-gray-400">Not provided</span>
                          )}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Github /> GitHub Profile</h3>
              <motion.div variants={elementEntranceVariants} className="profile-form-row full-width">
                <label className="profile-label">GitHub</label>
                {isEditMode ? (
                  <div className="flex flex-col gap-2">
                    <input
                      type="text"
                      value={profile.cpHandles?.github?.userId || ""}
                      onChange={(e) => handleCpHandleChange('github', 'userId', e.target.value)}
                      className="profile-input"
                      placeholder="Username (e.g., octocat)"
                    />
                    <input
                      type="text"
                      value={profile.cpHandles?.github?.url || ""}
                      onChange={(e) => handleCpHandleChange('github', 'url', e.target.value)}
                      className="profile-input"
                      placeholder="Full URL (e.g., https://github.com/octocat)"
                    />
                  </div>
                ) : (
                  <div className="flex gap-2 items-center">
                    {profile.cpHandles?.github?.userId ? (
                      <>
                        <span className="profile-input" style={{ flex: 1 }}>{profile.cpHandles.github.userId}</span>
                        <a
                          href={profile.cpHandles?.github?.url || deriveHandleUrl('github', profile.cpHandles.github)}
                          target="_blank"
                          rel="noreferrer"
                          className="btn-profile btn-profile-secondary flex items-center justify-center"
                        >
                          <Github size={16} />
                        </a>
                      </>
                    ) : (
                      <span className="profile-input text-gray-400">Not provided</span>
                    )}
                  </div>
                )}
              </motion.div>
            </motion.div>

            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Code /> Projects</h3>
              <motion.div variants={containerVariants} className="profile-list">
                {(Array.isArray(profile.projects) ? profile.projects : []).map((proj, idx) => (
                  <motion.div variants={itemVariants} key={idx} className="profile-list-item">
                    <div className="profile-list-item-header">
                      <div>
                        <h4 className="profile-list-item-title text-lg">{proj.name}</h4>
                        <p className="profile-list-item-text">{proj.description}</p>
                        {proj.techStack && <p className="profile-list-item-meta">Start Using: {proj.techStack}</p>}
                        <div className="flex gap-4 mt-2">
                          {proj.liveLink && <a href={proj.liveLink} target="_blank" rel="noreferrer" className="profile-link">Live Demo <ExternalLink size={14} /></a>}
                          {proj.repoLink && <a href={proj.repoLink} target="_blank" rel="noreferrer" className="profile-link">Repo <Github size={14} /></a>}
                        </div>
                      </div>
                      {isEditMode && <button onClick={() => removeProject(idx)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>}
                    </div>
                  </motion.div>
                ))}

                {isEditMode && (
                  <motion.div variants={elementEntranceVariants} className="profile-new-item-form profile-form-grid">
                    <div className="profile-form-row full-width">
                      <label className="profile-label">Project Name</label>
                      <input type="text" value={newProject.name} onChange={(e) => setNewProject({ ...newProject, name: e.target.value })} className="profile-input" />
                    </div>
                    <div className="profile-form-row full-width">
                      <label className="profile-label">Description</label>
                      <textarea value={newProject.description} onChange={(e) => setNewProject({ ...newProject, description: e.target.value })} className="profile-textarea" />
                    </div>
                    <div className="profile-form-row">
                      <label className="profile-label">Tech Stack</label>
                      <input type="text" value={newProject.techStack} onChange={(e) => setNewProject({ ...newProject, techStack: e.target.value })} className="profile-input" placeholder="React, Node, etc." />
                    </div>
                    <div className="profile-form-row">
                      <label className="profile-label">Live Link</label>
                      <input type="text" value={newProject.liveLink} onChange={(e) => setNewProject({ ...newProject, liveLink: e.target.value })} className="profile-input" />
                    </div>
                    <div className="profile-form-row">
                      <label className="profile-label">Repo Link</label>
                      <input type="text" value={newProject.repoLink} onChange={(e) => setNewProject({ ...newProject, repoLink: e.target.value })} className="profile-input" />
                    </div>
                    <button onClick={() => addProject(newProject)} className="btn-profile btn-profile-accent full-width text-center justify-center">Add Project</button>
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          </motion.div>
        );

      case "professional":
        return (
          <motion.div
            variants={cardContainerVariants}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, y: -20 }}
            className="profile-container"
          >
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Briefcase /> Internships & Experience</h3>

              <motion.div variants={containerVariants} className="profile-list">
                {(Array.isArray(profile.internships) ? profile.internships : []).map((exp, idx) => (
                  <motion.div variants={itemVariants} key={idx} className="profile-list-item">
                    <div className="profile-list-item-header">
                      <div>
                        <h4 className="profile-list-item-title text-lg flex items-center gap-2">
                          {exp.companyName}
                          <span className={`profile-list-item-badge ${exp.type === 'internship' ? 'success' : 'info'}`}>{exp.type}</span>
                        </h4>
                        <p className="font-semibold text-sm text-gray-700 mt-1 mb-2">
                          <span className="profile-role-highlight">{exp.role}</span> <span className="text-gray-500">• {exp.duration}</span>
                        </p>
                        <p className="profile-list-item-text">{exp.description}</p>
                      </div>
                      {isEditMode && <button onClick={() => removeInternship(idx)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>}
                    </div>
                  </motion.div>
                ))}

                {isEditMode && (
                  <motion.div variants={elementEntranceVariants} className="profile-new-item-form profile-form-grid">
                    <div className="profile-form-row">
                      <label className="profile-label">Company / Org</label>
                      <input type="text" value={newInternshipDetails.companyName} onChange={(e) => setNewInternshipDetails({ ...newInternshipDetails, companyName: e.target.value })} className="profile-input" />
                    </div>
                    <div className="profile-form-row">
                      <label className="profile-label">Role</label>
                      <input type="text" value={newInternshipDetails.role} onChange={(e) => setNewInternshipDetails({ ...newInternshipDetails, role: e.target.value })} className="profile-input" />
                    </div>
                    <div className="profile-form-row">
                      <label className="profile-label">Duration</label>
                      <input type="text" value={newInternshipDetails.duration} onChange={(e) => setNewInternshipDetails({ ...newInternshipDetails, duration: e.target.value })} className="profile-input" placeholder="e.g. Jun 2023 - Aug 2023" />
                    </div>
                    <div className="profile-form-row">
                      <label className="profile-label">Type</label>
                      <select value={newInternshipDetails.type} onChange={(e) => setNewInternshipDetails({ ...newInternshipDetails, type: e.target.value })} className="profile-select">
                        <option value="internship">Internship</option>
                        <option value="freelance">Freelance</option>
                        <option value="volunteer">Volunteer</option>
                      </select>
                    </div>
                    <div className="profile-form-row full-width">
                      <label className="profile-label">Description</label>
                      <textarea value={newInternshipDetails.description} onChange={(e) => setNewInternshipDetails({ ...newInternshipDetails, description: e.target.value })} className="profile-textarea" />
                    </div>
                    <button onClick={() => addInternship(newInternshipDetails)} className="btn-profile btn-profile-accent full-width text-center justify-center">Add Experience</button>
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          </motion.div>
        );

      case "clubs":
        return (
          <motion.div
            variants={cardContainerVariants}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, y: -20 }}
            className="profile-container"
          >
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Users /> Clubs & Activities</h3>

              <motion.div variants={containerVariants} className="profile-list">
                {(Array.isArray(profile.clubs) ? profile.clubs : []).map((club, idx) => {
                  const clubName = typeof club === 'string' ? club : club.clubName;
                  const role = typeof club === 'string' ? '' : club.role;
                  const events = typeof club === 'string' ? '' : club.events;

                  // Inline edit mode for this entry
                  if (isEditMode && editingClubIdx === idx) {
                    const evtInputId = `editClubEventInput_${idx}`;
                    return (
                      <motion.div variants={itemVariants} key={idx} className="profile-list-item">
                        <div className="profile-new-item-form profile-form-grid" style={{ width: '100%' }}>
                          <div className="profile-form-row full-width">
                            <label className="profile-label">Club Name</label>
                            <input type="text" value={editingClubData.clubName} onChange={(e) => setEditingClubData({ ...editingClubData, clubName: e.target.value })} className="profile-input" />
                          </div>
                          <div className="profile-form-row full-width">
                            <label className="profile-label">Role</label>
                            <input type="text" value={editingClubData.role} onChange={(e) => setEditingClubData({ ...editingClubData, role: e.target.value })} className="profile-input" placeholder="Member, Lead, etc." />
                          </div>
                          <div className="profile-form-row full-width">
                            <label className="profile-label">Key Events/Activities</label>
                            <div className="profile-tags mb-2">
                              {(editingClubData.events || []).map((ev, i) => (
                                <span key={i} className="profile-tag profile-tag-tech">
                                  {ev}
                                  <button type="button" onClick={() => {
                                    const filtered = editingClubData.events.filter((_, index) => index !== i);
                                    setEditingClubData({ ...editingClubData, events: filtered });
                                  }} className="profile-tag-remove">×</button>
                                </span>
                              ))}
                            </div>
                            <div className="profile-add-row" style={{ marginTop: 0 }}>
                              <input type="text" id={evtInputId} className="profile-input" placeholder="Add an activity/event..." onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  if (e.target.value.trim()) {
                                    setEditingClubData({ ...editingClubData, events: [...(editingClubData.events || []), e.target.value.trim()] });
                                    e.target.value = '';
                                  }
                                }
                              }} />
                              <button type="button" onClick={() => {
                                const el = document.getElementById(evtInputId);
                                if (el && el.value.trim()) {
                                  setEditingClubData({ ...editingClubData, events: [...(editingClubData.events || []), el.value.trim()] });
                                  el.value = '';
                                }
                              }} className="btn-profile btn-profile-secondary"><Plus size={16} /></button>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '0.75rem', gridColumn: '1/-1', marginTop: '0.5rem' }}>
                            <button onClick={() => updateClub(idx, editingClubData)} className="btn-profile btn-profile-primary" style={{ flex: 1 }}><Check size={16} style={{ marginRight: 6 }} />Save Changes</button>
                            <button onClick={() => setEditingClubIdx(null)} className="btn-profile btn-profile-secondary" style={{ flex: 1 }}><X size={16} style={{ marginRight: 6 }} />Cancel</button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  }

                  return (
                    <motion.div variants={itemVariants} key={idx} className="profile-list-item">
                      <div className="profile-list-item-header">
                        <div style={{ flex: 1 }}>
                          <h4 className="profile-list-item-title text-lg">{clubName}</h4>
                          {role && <p className="text-sm mt-1 mb-2"><span className="profile-role-highlight">{role}</span></p>}
                          {Array.isArray(events) ? (
                            <ul className="profile-list-item-text list-disc list-inside mt-2">
                              {events.map((ev, i) => <li key={i}>{ev}</li>)}
                            </ul>
                          ) : (
                            events && <p className="profile-list-item-text">{events}</p>
                          )}
                        </div>
                        {isEditMode && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginLeft: '1rem' }}>
                            <button
                              onClick={() => {
                                setEditingClubIdx(idx);
                                setEditingClubData({
                                  clubName: clubName || '',
                                  role: role || '',
                                  events: Array.isArray(events) ? [...events] : (events ? [events] : [])
                                });
                              }}
                              className="btn-profile btn-profile-secondary"
                              style={{ padding: '6px', borderRadius: '50%', minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              title="Edit"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => removeClub(idx)}
                              className="text-red-500 hover:text-red-700"
                              style={{ padding: '6px', borderRadius: '50%', minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(239,68,68,0.1)', border: 'none', cursor: 'pointer' }}
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}

                {isEditMode && (
                  <motion.div variants={elementEntranceVariants} className="profile-new-item-form profile-form-grid" style={{ marginTop: '1rem', borderTop: '1px dashed var(--glass-border)', paddingTop: '1rem' }}>
                    <div style={{ gridColumn: '1/-1', marginBottom: '0.5rem', fontWeight: 'bold', opacity: 0.7, fontSize: '0.9rem' }}>➕ Add New Club / Activity</div>
                    <div className="profile-form-row full-width">
                      <label className="profile-label">Club Name</label>
                      <input type="text" value={newClubDetails.clubName} onChange={(e) => setNewClubDetails({ ...newClubDetails, clubName: e.target.value })} className="profile-input" />
                    </div>
                    <div className="profile-form-row full-width">
                      <label className="profile-label">Role</label>
                      <input type="text" value={newClubDetails.role} onChange={(e) => setNewClubDetails({ ...newClubDetails, role: e.target.value })} className="profile-input" placeholder="Member, Lead, etc." />
                    </div>
                    <div className="profile-form-row full-width">
                      <label className="profile-label">Key Events/Activities</label>
                      <div className="profile-tags mb-2">
                        {(newClubDetails.events || []).map((ev, i) => (
                           <span key={i} className="profile-tag profile-tag-tech">
                             {ev}
                             <button type="button" onClick={() => {
                               const filtered = newClubDetails.events.filter((_, index) => index !== i);
                               setNewClubDetails({ ...newClubDetails, events: filtered })
                             }} className="profile-tag-remove">×</button>
                           </span>
                        ))}
                      </div>
                      <div className="profile-add-row" style={{ marginTop: 0 }}>
                        <input type="text" id="newClubEventInput" className="profile-input" placeholder="Add an activity/event..." onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (e.target.value.trim()) {
                               setNewClubDetails({ ...newClubDetails, events: [...(newClubDetails.events || []), e.target.value.trim()] });
                               e.target.value = '';
                            }
                          }
                        }} />
                        <button type="button" onClick={() => {
                           const el = document.getElementById("newClubEventInput");
                           if (el && el.value.trim()) {
                               setNewClubDetails({ ...newClubDetails, events: [...(newClubDetails.events || []), el.value.trim()] });
                               el.value = '';
                           }
                        }} className="btn-profile btn-profile-secondary"><Plus size={16} /></button>
                      </div>
                    </div>
                    <button onClick={() => addClub(newClubDetails)} className="btn-profile btn-profile-accent full-width text-center justify-center">Add Club Activity</button>
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={pageEntranceVariants}
      className={`profile-page ${isEditMode ? 'is-editing' : ''}`}
    >
      {/* Header */}
      <motion.header variants={headerVariants} className="profile-header">
        <div className="profile-header-content">
          <h1 className="profile-title">
            <span className="profile-title-text gradient-text">VJ LINK</span>
          </h1>
          <div className="profile-controls">
            {!isViewingOther && (
              <button
                onClick={() => {
                  if (isEditMode) handleSave();
                  else setIsEditMode(true);
                }}
                className={`profile-toggle-btn ${isEditMode ? 'save-mode' : 'view-mode'}`}
              >
                {isEditMode ? <><Save size={18} /> Save Changes</> : <><Edit2 size={18} /> Edit Profile</>}
              </button>
            )}
            <button
              onClick={() => navigate('/home')}
              title="Home"
              style={{
                  width: 46, height: 46, borderRadius: '14px',
                  border: '1px solid var(--glass-border)', background: 'var(--glass-bg)',
                  color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginLeft: '0.5rem', flexShrink: 0
              }}
            >
              <Home size={20} />
            </button>
            <button
              onClick={() => {
                localStorage.removeItem("vjconnect_user");
                navigate("/");
              }}
              title="Logout"
              style={{
                  width: 46, height: 46, borderRadius: '14px',
                  border: 'none', background: '#ef4444',
                  color: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(239,68,68,0.4)', marginLeft: '0.5rem', flexShrink: 0
              }}
            >
              <LogOut size={20} />
            </button>


          </div>
        </div>
      </motion.header>

      {/* Tabs */}
      <motion.div variants={tabsVariants} className="profile-tabs">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`profile-tab ${selectedTab === tab.id ? 'active' : ''}`}
            >
              <Icon />
              <span className="profile-tab-label">{tab.label}</span>
            </button>
          );
        })}
      </motion.div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {renderTabContent()}
      </AnimatePresence>

      {/* Image Modal */}
      <AnimatePresence>
        {isImageModalOpen && profile.profile_pic && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="profile-image-modal-overlay"
            onClick={() => setIsImageModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="profile-image-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={profile.profile_pic} alt={profile.name} className="profile-full-image" />
              <button className="profile-modal-close" onClick={() => setIsImageModalOpen(false)}><X size={24} /></button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default Profile;
